"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.breadcrumbsIntegration = void 0;

var _browserUtils = require("@sentry-internal/browser-utils");

var _core = require("@sentry/core");

var _debugBuild = require("../debug-build.js");

var _helpers = require("../helpers.js");

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

/* eslint-disable max-lines */

/** maxStringLength gets capped to prevent 100 breadcrumbs exceeding 1MB event payload size */
const MAX_ALLOWED_STRING_LENGTH = 1024;
const INTEGRATION_NAME = 'Breadcrumbs';

const _breadcrumbsIntegration = (options = {}) => {
  const _options = _objectSpread({
    console: true,
    dom: true,
    fetch: true,
    history: true,
    sentry: true,
    xhr: true
  }, options);

  return {
    name: INTEGRATION_NAME,

    setup(client) {
      if (_options.console) {
        (0, _core.addConsoleInstrumentationHandler)(_getConsoleBreadcrumbHandler(client));
      }

      if (_options.dom) {
        (0, _browserUtils.addClickKeypressInstrumentationHandler)(_getDomBreadcrumbHandler(client, _options.dom));
      }

      if (_options.xhr) {
        (0, _browserUtils.addXhrInstrumentationHandler)(_getXhrBreadcrumbHandler(client));
      }

      if (_options.fetch) {
        (0, _core.addFetchInstrumentationHandler)(_getFetchBreadcrumbHandler(client));
      }

      if (_options.history) {
        (0, _browserUtils.addHistoryInstrumentationHandler)(_getHistoryBreadcrumbHandler(client));
      }

      if (_options.sentry) {
        client.on('beforeSendEvent', _getSentryBreadcrumbHandler(client));
      }
    }

  };
};

const breadcrumbsIntegration = exports.breadcrumbsIntegration = (0, _core.defineIntegration)(_breadcrumbsIntegration);
/**
 * Adds a breadcrumb for Sentry events or transactions if this option is enabled.
 */

function _getSentryBreadcrumbHandler(client) {
  return function addSentryBreadcrumb(event) {
    if ((0, _core.getClient)() !== client) {
      return;
    }

    (0, _core.addBreadcrumb)({
      category: `sentry.${event.type === 'transaction' ? 'transaction' : 'event'}`,
      event_id: event.event_id,
      level: event.level,
      message: (0, _core.getEventDescription)(event)
    }, {
      event
    });
  };
}
/**
 * A HOC that creates a function that creates breadcrumbs from DOM API calls.
 * This is a HOC so that we get access to dom options in the closure.
 */


function _getDomBreadcrumbHandler(client, dom) {
  return function _innerDomBreadcrumb(handlerData) {
    if ((0, _core.getClient)() !== client) {
      return;
    }

    let target;
    let componentName;
    let keyAttrs = typeof dom === 'object' ? dom.serializeAttribute : undefined;
    let maxStringLength = typeof dom === 'object' && typeof dom.maxStringLength === 'number' ? dom.maxStringLength : undefined;

    if (maxStringLength && maxStringLength > MAX_ALLOWED_STRING_LENGTH) {
      _debugBuild.DEBUG_BUILD && _core.logger.warn(`\`dom.maxStringLength\` cannot exceed ${MAX_ALLOWED_STRING_LENGTH}, but a value of ${maxStringLength} was configured. Sentry will use ${MAX_ALLOWED_STRING_LENGTH} instead.`);
      maxStringLength = MAX_ALLOWED_STRING_LENGTH;
    }

    if (typeof keyAttrs === 'string') {
      keyAttrs = [keyAttrs];
    } // Accessing event.target can throw (see getsentry/raven-js#838, #768)


    try {
      const event = handlerData.event;
      const element = _isEvent(event) ? event.target : event;
      target = (0, _core.htmlTreeAsString)(element, {
        keyAttrs,
        maxStringLength
      });
      componentName = (0, _core.getComponentName)(element);
    } catch (e) {
      target = '<unknown>';
    }

    if (target.length === 0) {
      return;
    }

    const breadcrumb = {
      category: `ui.${handlerData.name}`,
      message: target
    };

    if (componentName) {
      breadcrumb.data = {
        'ui.component_name': componentName
      };
    }

    (0, _core.addBreadcrumb)(breadcrumb, {
      event: handlerData.event,
      name: handlerData.name,
      global: handlerData.global
    });
  };
}
/**
 * Creates breadcrumbs from console API calls
 */


function _getConsoleBreadcrumbHandler(client) {
  return function _consoleBreadcrumb(handlerData) {
    if ((0, _core.getClient)() !== client) {
      return;
    }

    const breadcrumb = {
      category: 'console',
      data: {
        arguments: handlerData.args,
        logger: 'console'
      },
      level: (0, _core.severityLevelFromString)(handlerData.level),
      message: (0, _core.safeJoin)(handlerData.args, ' ')
    };

    if (handlerData.level === 'assert') {
      if (handlerData.args[0] === false) {
        breadcrumb.message = `Assertion failed: ${(0, _core.safeJoin)(handlerData.args.slice(1), ' ') || 'console.assert'}`;
        breadcrumb.data.arguments = handlerData.args.slice(1);
      } else {
        // Don't capture a breadcrumb for passed assertions
        return;
      }
    }

    (0, _core.addBreadcrumb)(breadcrumb, {
      input: handlerData.args,
      level: handlerData.level
    });
  };
}
/**
 * Creates breadcrumbs from XHR API calls
 */


function _getXhrBreadcrumbHandler(client) {
  return function _xhrBreadcrumb(handlerData) {
    if ((0, _core.getClient)() !== client) {
      return;
    }

    const startTimestamp = handlerData.startTimestamp,
          endTimestamp = handlerData.endTimestamp;
    const sentryXhrData = handlerData.xhr[_browserUtils.SENTRY_XHR_DATA_KEY]; // We only capture complete, non-sentry requests

    if (!startTimestamp || !endTimestamp || !sentryXhrData) {
      return;
    }

    const method = sentryXhrData.method,
          url = sentryXhrData.url,
          status_code = sentryXhrData.status_code,
          body = sentryXhrData.body;
    const data = {
      method,
      url,
      status_code
    };
    const hint = {
      xhr: handlerData.xhr,
      input: body,
      startTimestamp,
      endTimestamp
    };
    const level = (0, _core.getBreadcrumbLogLevelFromHttpStatusCode)(status_code);
    (0, _core.addBreadcrumb)({
      category: 'xhr',
      data,
      type: 'http',
      level
    }, hint);
  };
}
/**
 * Creates breadcrumbs from fetch API calls
 */


function _getFetchBreadcrumbHandler(client) {
  return function _fetchBreadcrumb(handlerData) {
    if ((0, _core.getClient)() !== client) {
      return;
    }

    const startTimestamp = handlerData.startTimestamp,
          endTimestamp = handlerData.endTimestamp; // We only capture complete fetch requests

    if (!endTimestamp) {
      return;
    }

    if (handlerData.fetchData.url.match(/sentry_key/) && handlerData.fetchData.method === 'POST') {
      // We will not create breadcrumbs for fetch requests that contain `sentry_key` (internal sentry requests)
      return;
    }

    if (handlerData.error) {
      const data = handlerData.fetchData;
      const hint = {
        data: handlerData.error,
        input: handlerData.args,
        startTimestamp,
        endTimestamp
      };
      (0, _core.addBreadcrumb)({
        category: 'fetch',
        data,
        level: 'error',
        type: 'http'
      }, hint);
    } else {
      const response = handlerData.response;

      const data = _objectSpread(_objectSpread({}, handlerData.fetchData), {}, {
        status_code: response && response.status
      });

      const hint = {
        input: handlerData.args,
        response,
        startTimestamp,
        endTimestamp
      };
      const level = (0, _core.getBreadcrumbLogLevelFromHttpStatusCode)(data.status_code);
      (0, _core.addBreadcrumb)({
        category: 'fetch',
        data,
        type: 'http',
        level
      }, hint);
    }
  };
}
/**
 * Creates breadcrumbs from history API calls
 */


function _getHistoryBreadcrumbHandler(client) {
  return function _historyBreadcrumb(handlerData) {
    if ((0, _core.getClient)() !== client) {
      return;
    }

    let from = handlerData.from;
    let to = handlerData.to;
    const parsedLoc = (0, _core.parseUrl)(_helpers.WINDOW.location.href);
    let parsedFrom = from ? (0, _core.parseUrl)(from) : undefined;
    const parsedTo = (0, _core.parseUrl)(to); // Initial pushState doesn't provide `from` information

    if (!parsedFrom || !parsedFrom.path) {
      parsedFrom = parsedLoc;
    } // Use only the path component of the URL if the URL matches the current
    // document (almost all the time when using pushState)


    if (parsedLoc.protocol === parsedTo.protocol && parsedLoc.host === parsedTo.host) {
      to = parsedTo.relative;
    }

    if (parsedLoc.protocol === parsedFrom.protocol && parsedLoc.host === parsedFrom.host) {
      from = parsedFrom.relative;
    }

    (0, _core.addBreadcrumb)({
      category: 'navigation',
      data: {
        from,
        to
      }
    });
  };
}

function _isEvent(event) {
  return !!event && !!event.target;
}